import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { formatDateTime } from '../lib/format';
import { EmptyState } from '../components/EmptyState';
import { ScannedAt } from '../components/ScannedAt';
import { useUrlNumber } from '../lib/useUrlState';

/**
 * Network → Connection Audit. The network-truth log of staff→device reaches
 * (ssh / adb / vnc) captured at the VPN gateway by wg-orchestrator's conntrack
 * sidecar and appended to the PostgreSQL `connection_audit` table.
 *
 * Ported from optisigns-staff-app (`features/network/ConnectionAuditPage`).
 *
 * ROOT-ONLY: this is the SOC2 audit-of-record, so the backing query is
 * `@RequireRole('root')`. The route is gated to root as well — a non-root user
 * never sees the nav entry, and would get a GraphQL error if they hit the URL.
 *
 * The table is append-only upstream (the app role has INSERT+SELECT with
 * UPDATE/DELETE revoked), and this view is read-only.
 */

const VPN_CONNECTION_AUDIT = gql`
  query VpnConnectionAudit($filter: ConnectionAuditFilterInput) {
    vpnConnectionAudit(filter: $filter) {
      hasMore
      rows {
        ts
        region
        proto
        service
        srcIp
        dstIp
        dstPort
        result
        durationMs
        bytes
        srcSubnet
        dstSubnet
        srcEmail
        srcName
        dstDevice
        dstName
      }
    }
  }
`;

interface AuditRow {
  ts: string;
  region: string;
  proto: string;
  service: string;
  srcIp: string;
  dstIp: string;
  dstPort: number;
  result: string;
  durationMs?: number | null;
  bytes?: number | null;
  srcSubnet?: string | null;
  dstSubnet?: string | null;
  srcEmail?: string | null;
  srcName?: string | null;
  dstDevice?: string | null;
  dstName?: string | null;
}

const PAGE_SIZE = 100;

function fmtDuration(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtBytes(b?: number | null): string {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/** The four filter keys, in URL order. Kept as one list so the draft state, the
 *  committed query and Reset can't drift apart. */
const FILTER_KEYS = ['srcEmail', 'dstDevice', 'service', 'result'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export function VpnConnectionAuditPage() {
  // The URL is the APPLIED filter; the inputs below are a draft committed on
  // Apply, so we don't re-query per keystroke against a large partitioned table.
  // Putting the applied set in the URL is what makes "every ssh reach Binh made"
  // a link an auditor can be sent — the reason this page exists.
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useUrlNumber('page', 1);
  const applied = useMemo(() => {
    const out: Partial<Record<FilterKey, string>> = {};
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [params]);

  const [srcEmail, setSrcEmail] = useState(applied.srcEmail ?? '');
  const [dstDevice, setDstDevice] = useState(applied.dstDevice ?? '');
  const [service, setService] = useState(applied.service ?? 'all');
  const [result, setResult] = useState(applied.result ?? 'all');
  const [viewing, setViewing] = useState<AuditRow | null>(null);

  const offset = (page - 1) * PAGE_SIZE;
  const filter = useMemo(
    () => ({ ...applied, offset, limit: PAGE_SIZE }),
    [applied, offset],
  );

  const { data, loading, error } = useQuery<{
    vpnConnectionAudit: { rows: AuditRow[]; hasMore: boolean };
  }>(VPN_CONNECTION_AUDIT, { variables: { filter }, fetchPolicy: 'cache-and-network' });

  const rows = data?.vpnConnectionAudit.rows ?? [];
  const hasMore = data?.vpnConnectionAudit.hasMore ?? false;

  /** Draft → URL in ONE setParams. Composing per-key setters here would lose all
   *  but the last edit: each builds its next params from the same `prev`. Same
   *  trap `useUrlSort` documents. */
  function commit(next: Partial<Record<FilterKey, string>>) {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        for (const k of FILTER_KEYS) {
          const v = next[k];
          if (v) out.set(k, v);
          else out.delete(k);
        }
        out.delete('page'); // a new filter invalidates the page number
        return out;
      },
      { replace: true },
    );
  }

  function apply() {
    commit({
      srcEmail: srcEmail.trim(),
      dstDevice: dstDevice.trim(),
      service: service === 'all' ? '' : service,
      result: result === 'all' ? '' : result,
    });
  }

  function reset() {
    setSrcEmail('');
    setDstDevice('');
    setService('all');
    setResult('all');
    commit({});
  }

  return (
    <div className="page page--wide">
      <p className="muted">
        Network-truth log of staff→device reaches (ssh / adb / vnc), captured at the VPN gateway.
        Source: wg-orchestrator <code>connection_audit</code> (PostgreSQL, append-only). Read-only.
      </p>
      {/* This page is the SOC2 audit-of-record, so "how old is this?" is not a
          scan age — rows land as connections happen. Say that rather than
          borrowing the scanner's wording. */}
      <ScannedAt
        curated
        lead="Appended at the gateway as connections happen — not a scan, and never edited after the fact."
      />

      <div className="filter-row">
        <input
          type="search"
          aria-label="Person (email)"
          placeholder="Person — name@optisigns.com"
          value={srcEmail}
          onChange={(e) => setSrcEmail(e.target.value)}
        />
        <input
          type="search"
          aria-label="Device UUID"
          placeholder="Device UUID"
          value={dstDevice}
          onChange={(e) => setDstDevice(e.target.value)}
        />
        <select aria-label="Service" value={service} onChange={(e) => setService(e.target.value)}>
          <option value="all">Any service</option>
          <option value="ssh">ssh</option>
          <option value="adb">adb</option>
          <option value="vnc">vnc</option>
        </select>
        <select aria-label="Result" value={result} onChange={(e) => setResult(e.target.value)}>
          <option value="all">Any result</option>
          <option value="established">established</option>
          <option value="no-reply">no-reply</option>
        </select>
        <button className="btn btn-primary btn-sm" onClick={apply} disabled={loading}>
          Apply
        </button>
        <button className="btn btn-sm" onClick={reset} disabled={loading}>
          Reset
        </button>
      </div>

      {error ? (
        <>
          <p className="error">Couldn&apos;t load the audit log.</p>
          <p className="muted">
            Nothing is lost — the gateway keeps appending. If the error mentions{' '}
            <em>not configured</em>, this environment has no audit database wired up yet; ask in
            #devops-infra.
          </p>
        </>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="shield"
          title={loading ? 'Loading…' : 'No connections match these filters'}
          body="Rows appear when a staff peer reaches a device over ssh, adb, or vnc through the VPN gateway."
        />
      ) : (
        <>
          <table className="table-dense">
            <thead>
              <tr>
                <th>When</th>
                <th>Person</th>
                <th>Device</th>
                <th>Service</th>
                <th>Result</th>
                <th>Region</th>
                <th>Duration</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.ts}-${r.srcIp}-${r.dstIp}-${r.dstPort}-${i}`}>
                  <td className="text-xs">{formatDateTime(r.ts)}</td>
                  <td>
                    {r.srcEmail ? (
                      <>
                        <div>{r.srcEmail}</div>
                        {r.srcName && <div className="muted text-2xs">{r.srcName}</div>}
                      </>
                    ) : (
                      // wg-orchestrator enriches identity at connection START; an
                      // unresolved person-subnet source is logged upstream as an
                      // unattributable access.
                      <span className="muted">unattributed</span>
                    )}
                  </td>
                  <td>
                    {r.dstName || r.dstDevice ? (
                      <>
                        <div>{r.dstName || '(unnamed)'}</div>
                        {r.dstDevice && <div className="muted text-2xs">{r.dstDevice}</div>}
                      </>
                    ) : (
                      <span className="ip-cell">{r.dstIp}</span>
                    )}
                  </td>
                  <td>
                    <span className="badge">{r.service}</span>
                  </td>
                  <td>
                    <span className="dot-cell">
                      <span
                        className={`health-dot ${r.result === 'established' ? 'ok' : 'warn'}`}
                      />
                      <span className="text-xs">{r.result}</span>
                    </span>
                  </td>
                  <td className="muted text-xs">{r.region}</td>
                  <td className="muted text-xs">{fmtDuration(r.durationMs)}</td>
                  <td>
                    <button className="mini-btn" onClick={() => setViewing(r)}>
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Server-side paging over a partitioned table: it reports `hasMore`
              rather than a total, so there is no page COUNT to show — counting
              would mean a second full scan of the audit history. */}
          <div className="pager">
            <span className="muted">
              Showing {offset + 1}–{offset + rows.length}
              {hasMore ? '' : ' (end)'}
            </span>
            <div className="pager-btns">
              <button className="mini-btn" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                ‹ Prev
              </button>
              <span className="muted">Page {page}</span>
              <button className="mini-btn" disabled={!hasMore || loading} onClick={() => setPage(page + 1)}>
                Next ›
              </button>
            </div>
          </div>
        </>
      )}

      {viewing && <AuditDetail row={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** Full row detail — everything the audit table stores for one connection. */
function AuditDetail({ row, onClose }: { row: AuditRow; onClose: () => void }) {
  return (
    <div className="confirm-overlay" onClick={onClose} role="presentation">
      <div
        className="confirm-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Connection detail"
      >
        <div className="confirm-title">Connection detail</div>
        <div className="confirm-body">
          <div className="kv-grid">
            <Field label="When" value={formatDateTime(row.ts)} />
            <Field label="Region" value={row.region} />
            <Field label="Service" value={`${row.service} (${row.proto})`} />
            <Field label="Result" value={row.result} />
            <Field label="Person" value={row.srcEmail || 'unattributed'} />
            <Field label="Person name" value={row.srcName || '—'} />
            <Field label="Source IP" value={row.srcIp} />
            <Field label="Source subnet" value={row.srcSubnet || '—'} />
            <Field label="Device" value={row.dstName || '—'} />
            <Field label="Device UUID" value={row.dstDevice || '—'} />
            <Field label="Dest IP" value={`${row.dstIp}:${row.dstPort}`} />
            <Field label="Dest subnet" value={row.dstSubnet || '—'} />
            <Field label="Duration" value={fmtDuration(row.durationMs)} />
            <Field label="Bytes" value={fmtBytes(row.bytes)} />
          </div>
        </div>
        <div className="confirm-actions">
          <button className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* IPs, subnets and device UUIDs are resource identifiers, not code snippets, so
   they render in Plex Sans — the mono policy ratified in phase 6 §1. This used to
   take a `mono` flag; nothing in this dialog qualified for it. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <span className="kv-k">{label}</span>
      <span className="kv-v">{value}</span>
    </>
  );
}
